import React, { useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  CircularProgress,
} from "@mui/material";
import { format } from "date-fns";

const SurveyMetricsFilters = ({
  filters,
  onClientChange,
  onProjectChange,
  onSurveyChange,
  onDateRangeChange,
  clients,
  projects,
  surveys,
  loading,
}) => {
  const [datePreset, setDatePreset] = useState("currentMonth");

  const getDateRange = (preset) => {
    const today = new Date();
    const currentMonthStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      1,
    );
    const currentMonthEnd = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0,
    );
    const previousMonthStart = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1,
    );
    const previousMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(
      today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1),
    );
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
    const previousWeekStart = new Date(currentWeekStart);
    previousWeekStart.setDate(previousWeekStart.getDate() - 7);
    const previousWeekEnd = new Date(previousWeekStart);
    previousWeekEnd.setDate(previousWeekEnd.getDate() + 6);

    switch (preset) {
      case "currentMonth":
        return { startDate: currentMonthStart, endDate: currentMonthEnd };
      case "previousMonth":
        return { startDate: previousMonthStart, endDate: previousMonthEnd };
      case "currentWeek":
        return { startDate: currentWeekStart, endDate: currentWeekEnd };
      case "previousWeek":
        return { startDate: previousWeekStart, endDate: previousWeekEnd };
      case "custom":
        return { custom: true };
      default:
        return { startDate: currentMonthStart, endDate: currentMonthEnd };
    }
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== "custom") {
      const { startDate, endDate } = getDateRange(preset);
      onDateRangeChange(startDate, endDate);
    }
  };

  const handleCustomDateChange = (field, value) => {
    if (field === "startDate") {
      onDateRangeChange(new Date(value), filters.endDate);
    } else {
      onDateRangeChange(filters.startDate, new Date(value));
    }
  };

  return (
    <Box sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
        {/* CLIENT FILTER */}
        <FormControl
          fullWidth
          disabled={loading}
        >
          <InputLabel>Client</InputLabel>
          <Select
            multiple
            value={filters.clientIds || []}
            onChange={(e) => onClientChange(e.target.value)}
            label="Client"
            renderValue={(selected) =>
              selected.length > 0
                ? `${selected.length} client${selected.length > 1 ? "s" : ""}`
                : "All clients"
            }
          >
            {clients.map((client) => (
              <MenuItem
                key={client.id}
                value={client.id}
              >
                {client.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* PROJECT FILTER */}
        <FormControl
          fullWidth
          disabled={
            loading || !filters.clientIds || filters.clientIds.length === 0
          }
        >
          <InputLabel>Project</InputLabel>
          <Select
            multiple
            value={filters.projectIds || []}
            onChange={(e) => onProjectChange(e.target.value)}
            label="Project"
            renderValue={(selected) =>
              selected.length > 0
                ? `${selected.length} project${selected.length > 1 ? "s" : ""}`
                : "All projects"
            }
          >
            {projects.map((project) => (
              <MenuItem
                key={project.id}
                value={project.id}
              >
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* SURVEY FILTER */}
        <FormControl
          fullWidth
          disabled={
            loading || !filters.projectIds || filters.projectIds.length === 0
          }
        >
          <InputLabel>Survey</InputLabel>
          <Select
            multiple
            value={filters.surveyIds || []}
            onChange={(e) => onSurveyChange(e.target.value)}
            label="Survey"
            renderValue={(selected) =>
              selected.length > 0
                ? `${selected.length} survey${selected.length > 1 ? "s" : ""}`
                : "All surveys"
            }
          >
            {surveys.map((survey) => (
              <MenuItem
                key={survey.id}
                value={survey.id}
              >
                {survey.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* DATE RANGE SECTION */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          {[
            { label: "Current Month", value: "currentMonth" },
            { label: "Previous Month", value: "previousMonth" },
            { label: "Current Week", value: "currentWeek" },
            { label: "Previous Week", value: "previousWeek" },
            { label: "Custom", value: "custom" },
          ].map((preset) => (
            <Chip
              key={preset.value}
              label={preset.label}
              onClick={() => handlePresetChange(preset.value)}
              variant={datePreset === preset.value ? "filled" : "outlined"}
              color={datePreset === preset.value ? "primary" : "default"}
              disabled={loading}
            />
          ))}
        </Box>

        {datePreset === "custom" && (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              type="date"
              label="Start Date"
              value={
                filters.startDate
                  ? format(new Date(filters.startDate), "yyyy-MM-dd")
                  : ""
              }
              onChange={(e) =>
                handleCustomDateChange("startDate", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              fullWidth
            />
            <TextField
              type="date"
              label="End Date"
              value={
                filters.endDate
                  ? format(new Date(filters.endDate), "yyyy-MM-dd")
                  : ""
              }
              onChange={(e) =>
                handleCustomDateChange("endDate", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              fullWidth
            />
          </Box>
        )}

        {(filters.startDate || filters.endDate) && datePreset !== "custom" && (
          <Box sx={{ mt: 1, fontSize: "0.875rem", color: "text.secondary" }}>
            {format(new Date(filters.startDate), "MMM d, yyyy")} to{" "}
            {format(new Date(filters.endDate), "MMM d, yyyy")}
          </Box>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <span>Loading filters...</span>
        </Box>
      )}
    </Box>
  );
};

export default SurveyMetricsFilters;
